import Vue, { PropType } from 'vue'
import { ExtendedVue } from 'vue/types/vue'

import { Language, LgVersion, LgVersioned } from '@/types/languages'

interface ViewModelData {
  versionsModified: boolean
}

interface ViewModelMethods {
  addLgVersion(lgCode: string, oldLgCode: string | undefined): boolean
  setLanguage(lgCode: string): void
}

interface ViewModelComputed<TLgVersion extends LgVersion> {
  show: boolean
  lgModel: TLgVersion
  isNew: boolean
}

interface ViewModelProps<TModel extends LgVersioned, TLgVersion extends LgVersion> {
  model: TModel
  language: Language
  createLgVersion: (
    lgCode: string,
    currentVersion: TLgVersion | undefined,
    model: TModel
  ) => TLgVersion | null
}

export function VersionedViewModelBase<
  TModel extends LgVersioned,
  TLgVersion extends LgVersion
> (): ExtendedVue<
  Vue,
  ViewModelData,
  ViewModelMethods,
  ViewModelComputed<TLgVersion>,
  ViewModelProps<TModel, TLgVersion>,
  void
  > {
  return Vue.extend({
    name: 'VersionedViewModelBase',

    props: {
      model: {
        type: Object as PropType<TModel>,
        required: true
      },
      language: {
        type: Object as PropType<Language>,
        required: true
      },
      createLgVersion: {
        type: Function as PropType<
          (
            lgCode: string,
            currentVersion: TLgVersion | undefined,
            model: TModel
          ) => TLgVersion | null
        >,
        required: false
      }
    },

    data: () => {
      return {
        versionsModified: false as boolean
      }
    },

    computed: {
      show (): boolean {
        return (
          !!this.model &&
          !!this.language &&
          this.model.lgVersions.some(x => x.lgCode == this.language.lgCode)
        )
      },

      lgModel (): TLgVersion {
        return this.model.lgVersions.find(x => x.lgCode == this.language.lgCode) as TLgVersion
      },

      isNew (): boolean {
        return false
      }
    },

    watch: {
      'language': {
        handler (language: Language, oldLanguage: Language): void {
          if (!language) return
          if (!this.model.lgVersions.some(x => x.lgCode == language.lgCode)) {
            if (this.isNew && this.model.lgVersions.length == 1 && !this.versionsModified) {
              this.model.lgVersions[0].lgCode = language.lgCode
              this.$nextTick(() => {
                this.versionsModified = false
              })
            } else {
              const success = this.addLgVersion(
                language.lgCode,
                oldLanguage ? oldLanguage.lgCode : undefined
              )
              if (!success) {
                if (oldLanguage) this.setLanguage(oldLanguage.lgCode)
                else if (this.model.lgVersions.length > 0)
                  this.setLanguage(this.model.lgVersions[0].lgCode)
              }
            }
          }
        },
        immediate: true
      },

      'model.lgVersions': {
        handler (lgVersions: TLgVersion[]): void {
          this.versionsModified = true
        },
        deep: true
      }
    },

    methods: {
      addLgVersion (lgCode: string, oldLgCode: string | undefined): boolean {
        if (!this.createLgVersion) return false

        const currentVersion = oldLgCode
          ? (this.model.lgVersions.find(x => x.lgCode == oldLgCode) as TLgVersion | undefined)
          : undefined

        const newVersion = this.createLgVersion(lgCode, currentVersion, this.model)
        if (newVersion == null) return false

        if (!this.model.lgVersions.includes(newVersion))
          this.model.lgVersions.splice(this.model.lgVersions.length, 0, newVersion)
        return true
      },

      setLanguage (lgCode: string): void {
        this.$emit('set-language', lgCode)
      }
    }
  })
}
